import { StateRouteEntry } from '@state-routing/models/state-route.models';

export const STATE_ROUTE_ROOT: string = '/avatar';
export const MAX_STATE_ROUTE_DEPTH: number = 8;

const TRANSIENT_ROUTE_PATTERNS: readonly RegExp[] = [
  /^\/login(?:\/|$)/,
  /^\/registration(?:\/|$)/,
  /^\/incoming-call(?:\/|$)/,
  /^\/audio-call(?:\/|$)/,
  /^\/avatar\/video-call(?:\/|$)/,
  /^\/credit-info(?:\/|$)/,
  /^\/invite(?:\/|$)/,
  /^\/pyjam-animate(?:\/|$)/,
  /^\/reply\/[^/]+\/payment(?:-result)?(?:\/|$)/,
  /^\/extra-work\/[^/]+\/payment(?:\/|$)/,
];

export function isInternalStateRouteUrl(url: string): boolean {
  const normalizedUrl: string = url.trim();
  if (!normalizedUrl || normalizedUrl.startsWith('//')) return false;
  if (normalizedUrl.includes('\\') || /[\u0000-\u001F\u007F]/.test(normalizedUrl)) return false;
  return !/^[a-z][a-z\d+.-]*:/i.test(normalizedUrl);
}

export function normalizeStateRouteUrl(url: string): string {
  const trimmedUrl: string = url.trim();
  if (!isInternalStateRouteUrl(trimmedUrl)) return STATE_ROUTE_ROOT;

  const normalizedUrl: string = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
  const queryIndex: number = normalizedUrl.indexOf('?');
  const hashIndex: number = normalizedUrl.indexOf('#');
  const suffixIndex: number = Math.min(
    queryIndex >= 0 ? queryIndex : normalizedUrl.length,
    hashIndex >= 0 ? hashIndex : normalizedUrl.length,
  );
  const path: string = normalizeStateRoutePath(normalizedUrl.slice(0, suffixIndex));
  return `${path}${normalizedUrl.slice(suffixIndex)}`;
}

export function normalizeStateRoutePath(url: string): string {
  const queryIndex: number = url.indexOf('?');
  const hashIndex: number = url.indexOf('#');
  const endIndex: number = Math.min(
    queryIndex >= 0 ? queryIndex : url.length,
    hashIndex >= 0 ? hashIndex : url.length,
  );
  const rawPath: string = url.slice(0, endIndex);
  if (!rawPath || rawPath === '/') return STATE_ROUTE_ROOT;

  const path: string = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

export function isStateRouteTransient(url: string): boolean {
  const path: string = normalizeStateRoutePath(url);
  return TRANSIENT_ROUTE_PATTERNS.some((pattern: RegExp): boolean => pattern.test(path));
}

export function createStateRouteEntry(url: string): StateRouteEntry {
  const normalizedUrl: string = normalizeStateRouteUrl(url);
  return {
    path: normalizeStateRoutePath(normalizedUrl),
    url: normalizedUrl,
  };
}

export function resolveStateRouteBranch(path: string): string {
  const segments: string[] = normalizeStateRoutePath(path).split('/').filter(Boolean);
  const firstSegment: string = segments[0] ?? 'avatar';
  return firstSegment.startsWith('@') ? 'people' : firstSegment;
}

export function isContextualStateRoute(path: string, currentPath: string): boolean {
  const normalizedPath: string = normalizeStateRoutePath(path);
  const normalizedCurrentPath: string = normalizeStateRoutePath(currentPath);

  if (normalizedPath.startsWith('/avatar/vplay/')) {
    return normalizedCurrentPath !== STATE_ROUTE_ROOT;
  }
  if (normalizedCurrentPath.startsWith('/avatar/vplay/')) {
    return isProfileStateRoute(normalizedPath);
  }
  if (normalizedCurrentPath.startsWith('/chats/')) {
    return isProfileStateRoute(normalizedPath);
  }
  return false;
}

export function resolveStateRouteParentPaths(path: string): readonly string[] {
  const normalizedPath: string = normalizeStateRoutePath(path);
  const segments: string[] = normalizedPath.split('/').filter(Boolean);
  if (segments.length <= 1) return [];

  if (normalizedPath.startsWith('/chats/')) {
    return resolveChatParents(segments);
  }
  if (normalizedPath.startsWith('/people/')) {
    return resolvePeopleParents(segments);
  }
  if (normalizedPath.startsWith('/@')) {
    return ['/people'];
  }
  if (normalizedPath === '/profile/settings/phone-change-confirm') {
    return ['/profile', '/profile/settings', '/profile/settings/personal-info'];
  }
  if (/^\/profile\/settings\/user-skills\/[^/]+$/.test(normalizedPath)) {
    return ['/profile', '/profile/settings', '/profile/settings/user-skills'];
  }
  if (normalizedPath.startsWith('/profile/settings/')) {
    return ['/profile', '/profile/settings'];
  }
  if (normalizedPath.startsWith('/profile/')) {
    return ['/profile'];
  }
  if (normalizedPath.startsWith('/tasks/')) {
    return resolveTaskParents(segments);
  }
  if (normalizedPath.startsWith('/reply/')) {
    return resolveReplyParents(segments);
  }
  if (normalizedPath.startsWith('/extra-work/')) {
    return resolveExtraWorkParents(segments);
  }
  return [];
}

function isProfileStateRoute(path: string): boolean {
  return path === '/profile'
    || path.startsWith('/profile/')
    || path.startsWith('/people/')
    || path.startsWith('/@');
}

function resolveChatParents(segments: readonly string[]): readonly string[] {
  if (
    segments.length <= 2
    || segments[1] === 'group'
    || segments[1] === 'share'
    || segments[1] === 'user'
  ) {
    return ['/chats'];
  }
  return ['/chats', `/${segments.slice(0, 2).join('/')}`];
}

function resolvePeopleParents(segments: readonly string[]): readonly string[] {
  return segments.length >= 4 && segments[2] === 'file'
    ? ['/people', `/${segments.slice(0, 2).join('/')}`]
    : ['/people'];
}

function resolveTaskParents(segments: readonly string[]): readonly string[] {
  const flowSegment: string = segments[1] ?? '';
  if (['add', 'draft', 'filter', 'choose-category', 'my-tasks'].includes(flowSegment)) {
    return ['/tasks'];
  }
  if (['edit', 'dispute'].includes(flowSegment)) {
    return segments.length >= 3 ? ['/tasks', `/tasks/${segments[2]}`] : ['/tasks'];
  }
  const parents: string[] = ['/tasks'];
  if (segments.length > 2) {
    parents.push(`/${segments.slice(0, 2).join('/')}`);
  }
  return parents;
}

function resolveReplyParents(segments: readonly string[]): readonly string[] {
  if (segments.length <= 2) return [];
  return [`/${segments.slice(0, 2).join('/')}`];
}

function resolveExtraWorkParents(segments: readonly string[]): readonly string[] {
  if (segments.length <= 2) return [];
  return [`/${segments.slice(0, 2).join('/')}`];
}
